import { Icon } from "@iconify/react/dist/iconify.js";
import { useState } from "react";

const ExportToCSV = ({ data, selectedChat, selectedRows }) => {
    const [isExporting, setIsExporting] = useState(false);

    const convertToCSV = (data) => {
        if (!data || data.length === 0) return '';

        // Define headers
        const headers = [
            'Conversation ID',
            'Session ID',
            'Start Time',
            'Message Count',
            'User Sentiment',
            'Success Score',
            'Score Status',
            'Resolution Status',
            'Feedback Exists',
            'Conversation'
        ];

        // Convert data to CSV format
        const csvContent = [
            headers.join(','),
            ...data.map(chat => [
                chat.conversation_id || '',
                chat.metadata?.session_id || '',
                chat.metadata?.timestamp || '',
                chat.analytics?.message_count || '',
                chat.analytics?.user_sentiment || '',
                chat.evaluation?.success_score || '',
                chat.evaluation?.success_score >= 7 ? 'Pass' : chat.evaluation?.success_score >= 4 ? 'Partial' : 'Fail',
                chat.analytics?.resolution_status || '',
                chat.feedback_exists ? 'Yes' : 'No',
                `"${(JSON.stringify(chat.conversation || []) || '').replace(/"/g, '""')}"` // Escape quotes in conversation
            ].join(','))
        ].join('\n');

        return csvContent;
    };

    const getEquipmentList = (bookingInfo) => {
        if (!bookingInfo || !Array.isArray(bookingInfo)) return '';
        return bookingInfo.map(item => item.equipment_name || item.name || 'Unknown').join(', ');
    };

    const getOrderStatus = (bookingInfo) => {
        if (!bookingInfo || !Array.isArray(bookingInfo)) return '';
        const statuses = bookingInfo.map(item => item.status || 'Unknown');
        return [...new Set(statuses)].join(', ');
    };

    const handleExport = async () => {
        if (!data || data.length === 0) {
            alert('No chat data available to export');
            return;
        }

        setIsExporting(true);

        try {
            let exportData = data;

            // If specific rows are selected, filter to those
            if (selectedRows && selectedRows.length > 0) {
                exportData = data.filter(chat => selectedRows.includes(chat.conversation_id));
            }

            // If a specific chat is selected, export only that
            if (selectedChat) {
                exportData = [selectedChat];
            }

            const csvContent = convertToCSV(exportData);
            
            // Create and download file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            
            link.setAttribute('href', url);
            link.setAttribute('download', `chat-history-${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting CSV:', error);
            alert('Error exporting data. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    const getExportLabel = () => {
        if (selectedChat) return 'Export Chat';
        if (selectedRows && selectedRows.length > 0) return `Export Selected (${selectedRows.length})`;
        return 'Export All';
    };

    return (
        <button
            onClick={handleExport}
            disabled={isExporting || !data || data.length === 0}
            className="flex items-center gap-1 px-2 py-1 sm:px-3 sm:py-2 button-container text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={getExportLabel()}
        >
            <Icon 
                icon={isExporting ? "eos-icons:loading" : "material-symbols:download"} 
                className={`text-lg sm:text-xl text-accent ${isExporting ? 'animate-spin' : ''}`} 
            />
            <span className="hidden sm:inline">
                {isExporting ? 'Exporting...' : getExportLabel()}
            </span>
        </button>
    );
};

export default ExportToCSV;
